import * as React from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/States";
import { useUser } from "../account/AccountStore";
import { NavItem, NavSection } from "./NavItem";
import { visibleGroups } from "./navigation";
import { Header } from "./Header";
import { routes } from "./routes";
import { NotFound } from "./NotFound";

/**
 * The application shell: sidebar, header, and the routed content area.
 *
 * Ported from ScadsysIdentity's home.tsx, which is the app it should have looked like all along -
 * this app previously had no shell at all, just a coloured tab strip rendered into a half-used
 * two-pane layout with no index route, so landing on "/" showed an empty panel.
 *
 * Two layout details are load-bearing and were bug fixes in the screens this replaces:
 *   - The main column is a flex column with `min-h-0`, not `h-full`. Without `min-h-0` a flex child
 *     refuses to shrink below its content, so the inner scroll container never scrolls and the whole
 *     page scrolls instead - which loses the sticky table headers the mark grid depends on.
 *   - Scrolling belongs to <main>, not to <body>. The sidebar has to stay put while content moves.
 */

export const Shell: React.FC = () => {
    const user = useUser();
    // Open by default on a desktop-sized screen, closed on a phone.
    const [sidebarOpen, setSidebarOpen] = React.useState(() => (typeof window === "undefined" ? true : window.innerWidth >= 1024));

    const groups = React.useMemo(() => visibleGroups(user), [user]);
    const closeOnMobile = () => {
        if (window.innerWidth < 1024) setSidebarOpen(false);
    };

    // /account/me answers 200 with an anonymous ClaimsIdentity rather than a 401, so the axios
    // interceptor never fires on first load and an unauthenticated visitor would otherwise sit on an
    // empty shell. Send them to the server-side OIDC challenge instead.
    const signedOut = user.resolved && !user.isAuthenticated;
    React.useEffect(() => {
        if (!signedOut) return;
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = "/Account/SignIn?redirectUrl=" + encodeURIComponent(returnTo);
    }, [signedOut]);

    // The account call has not come back yet - show a spinner rather than a nav with nothing in it.
    if (!user.resolved || signedOut) {
        return (
            <div className="flex h-full items-center justify-center bg-canvas">
                <LoadingState label={signedOut ? "Signing you in..." : "Loading your session..."} />
            </div>
        );
    }

    return (
        <div className="flex h-full bg-canvas">
            <aside
                id="shell-navigation"
                aria-label="Application navigation"
                className={`${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } fixed inset-y-0 left-0 z-50 flex w-64 shrink-0 flex-col border-r border-border bg-surface transition-transform duration-300 ease-in-out lg:static lg:translate-x-0`}
            >
                <div className="flex h-[62px] shrink-0 items-center border-b border-border px-5">
                    <NavLink to="/" onClick={closeOnMobile} className="flex min-w-0 items-center gap-2.5 no-underline">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-text">
                            <Icon icon={["fad", "graduation-cap"]} />
                        </span>
                        <span className="min-w-0">
                            <span className="block truncate text-[15px] font-bold leading-tight tracking-tight text-text-strong">
                                {user.organization?.name ?? "Scadsys"}
                            </span>
                            <span className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.14em] text-text-weak">Assessment</span>
                        </span>
                    </NavLink>
                </div>

                {/* min-h-0 lets this scroll independently instead of stretching the sidebar. */}
                <nav aria-label="Main navigation" className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 pb-6 scrollbar-custom">
                    <div className="pt-3">
                        <NavItem to="/" icon={["fad", "house"]} label="Dashboard" matchNested={false} onNavigate={closeOnMobile} />
                    </div>

                    {groups.map(({ group, entries }) => (
                        <div key={group.name}>
                            <NavSection label={group.name} />
                            {entries.map((entry) => (
                                <NavItem
                                    key={entry.route}
                                    to={entry.route}
                                    icon={entry.icon}
                                    label={entry.name}
                                    onNavigate={closeOnMobile}
                                />
                            ))}
                        </div>
                    ))}

                    {groups.length === 0 && (
                        <p className="px-3 py-6 text-sm text-text-weak">
                            You do not have permission to any part of this application for this organisation.
                        </p>
                    )}
                </nav>
            </aside>

            {sidebarOpen && (
                <div className="fixed inset-0 z-40 bg-neutral-900/50 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
            )}

            {/* min-w-0 stops a wide table inside the content from pushing the sidebar off screen. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <Header user={user} sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen((open) => !open)} />

                <main className="min-h-0 flex-1 overflow-y-auto scrollbar-custom">
                    <Routes>
                        {routes.map((route) => (
                            <Route key={route.path} path={route.path} element={route.element} />
                        ))}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default Shell;
